import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CustomFormBuilder from "./CustomFormBuilder";

import {
  createQuestionnaire,
  generateQuestionnaireQid,
} from "../../services/questionnaireService";
import { AppToast } from "../../components/AppFeedback";
import { normalizeQuestionSchemaForBackend } from "./questionnaireSchemaUtils";

const QuestionnaireCreateForm = ({ open, onClose, onCreated }) => {
  const [formState, setFormState] = useState({
    qid: "",
    name: "",
    label: "",
  });
  const [generatingQid, setGeneratingQid] = useState(false);
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState("");
  const initialJson = { pages: [{ name: "page1", elements: [] }] };
  const [formData, setFormData] = useState(initialJson);
  const [fullView, setFullView] = useState(false);
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Debug: confirm dialog open and builder mount
  useEffect(() => {
    if (open) console.log("[QuestionnaireCreateForm] dialog opened, CustomFormBuilder will mount");
  }, [open]);

  // Auto-generate QID from name + label with debounce
  useEffect(() => {
    if (!open) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const name = formState.name.trim();
    const label = formState.label.trim();
    if (!name || !label) {
      setFormState((prev) => ({ ...prev, qid: "" }));
      return;
    }

    setGeneratingQid(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const data = await generateQuestionnaireQid(name, label);
        setFormState((prev) => ({ ...prev, qid: data?.qid || "" }));
      } catch (error) {
        setError(error.message || "Failed to generate QID");
      } finally {
        setGeneratingQid(false);
      }
    }, 350);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formState.name, formState.label, open]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const hasFormItems =
    formData &&
    formData.pages &&
    Array.isArray(formData.pages) &&
    formData.pages.some((p) => p.elements && Array.isArray(p.elements) && p.elements.length > 0);

  const getSchemaFromCreator = useCallback(() => {
    try {
      if (!formData || !formData.pages) return initialJson;
      return formData;
    } catch (error) {
      console.error("Error getting schema from form:", error);
      return initialJson;
    }
  }, [formData]);

  const handleCreate = async () => {
    // Validation
    if (!formState.name.trim()) {
      setError("Questionnaire name is required");
      return;
    }

    if (!formState.label.trim()) {
      setError("Questionnaire label is required");
      return;
    }

    const rawSchema = getSchemaFromCreator();
    const hasSchema =
      rawSchema &&
      rawSchema.pages &&
      rawSchema.pages.some((p) => p.elements && p.elements.length > 0);
    if (!hasSchema) {
      setError("Please add at least one field to the form.");
      return;
    }

    setCreating(true);
    setError("");

    const question_schema = normalizeQuestionSchemaForBackend(rawSchema);

    try {
      const payload = {
        name: formState.name.trim(),
        label: formState.label.trim(),
        question_schema,
      };

      await createQuestionnaire(payload);
      setSnackbar({
        open: true,
        message: "Questionnaire created successfully. PDF is being generated in background.",
        severity: "success",
      });

      // Reset form
      setFormState({ qid: "", name: "", label: "" });
      
      // Notify parent and close
      setTimeout(() => {
        onCreated?.();
        onClose();
      }, 1500);
    } catch (error) {
      setError(error.message || "Failed to create questionnaire");
      setSnackbar({
        open: true,
        message: error.message || "Failed to create questionnaire",
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setFormState({ qid: "", name: "", label: "" });
      setError("");
      setFormData(initialJson);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={fullView ? false : "lg"}
        fullWidth={!fullView}
        PaperProps={{
          sx: {
            minHeight: fullView ? "98vh" : "92vh",
            maxHeight: "98vh",
            height: fullView ? "98vh" : undefined,
            display: "flex",
            flexDirection: "column",
            width: fullView ? "98vw" : "95vw",
            maxWidth: fullView ? "98vw" : "1400px",
          },
        }}
      >
        <DialogTitle sx={{ pr: fullView ? 10 : 6 }}>
          {fullView ? "Form builder" : "Create New Questionnaire"}
          {fullView && (
            <IconButton
              aria-label="Exit full view"
              onClick={() => setFullView(false)}
              sx={{ position: "absolute", right: 44, top: 8 }}
              title="Exit full view"
            >
              <FullscreenExitIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={creating}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...(fullView && { p: 0, overflow: "hidden" }),
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!fullView && (
            <>
              {/* Top Section - Form Fields */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      disabled
                      value={formState.qid}
                      label="QID (Auto-generated)"
                      InputProps={{ readOnly: true }}
                      helperText={
                        generatingQid
                          ? "Generating QID..."
                          : "Auto-generated from identifier + display name (e.g. PATINT01)"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      name="name"
                      label="Questionnaire Name (Identifier)"
                      placeholder="e.g., subject_intake"
                      value={formState.name}
                      onChange={handleInputChange}
                      disabled={creating}
                      helperText="Unique identifier; first 3 letters used in QID"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      name="label"
                      label="Questionnaire Label (Display Name)"
                      placeholder="e.g., Subject Intake Form"
                      value={formState.label}
                      onChange={handleInputChange}
                      disabled={creating}
                      helperText="Form heading shown to users; first 3 letters used in QID"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Build your form - heading with full view icon on right */}
              <Box
                sx={{
                  borderBottom: "2px solid #3f7fbf",
                  mb: 2,
                  pb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" sx={{ color: "#2f3e46", fontWeight: 600 }}>
                  Build your form
                </Typography>
                <IconButton
                  aria-label="Full view"
                  onClick={() => setFullView(true)}
                  sx={{ p: 0.5 }}
                  title="Full view (expand builder)"
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>
            </>
          )}

          {/* Form Builder Section - fills modal in fullView */}
          {open && (
            <Box
              sx={{
                flex: 1,
                minHeight: fullView ? 0 : 480,
                height: fullView ? "100%" : 480,
                width: "100%",
                overflow: "hidden",
                border: fullView ? "none" : "2px solid #3f7fbf",
                borderRadius: fullView ? 0 : 2,
                backgroundColor: fullView ? "#ffffff" : "#cfe0e8",
                position: "relative",
                display: "flex",
              }}
            >
              <CustomFormBuilder
                data={formData}
                onChange={(data) => setFormData(data)}
              />
            </Box>
          )}

          {!fullView && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "#dbe6ec", borderRadius: 2, border: "1px solid #3f7fbf" }}>
              <Typography variant="caption" sx={{ color: "#2f3e46" }}>
                💡 Use the form builder to add and configure questions, then <strong>Create Questionnaire</strong> to save. Click the fullscreen icon to expand the builder.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {!fullView && (
          <DialogActions sx={{ p: 2, borderTop: "2px solid #3f7fbf" }}>
            <Button onClick={handleClose} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={
                creating ||
                !formState.name.trim() ||
                !formState.label.trim()
              }
            >
              {creating ? "Creating..." : "Create Questionnaire"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default QuestionnaireCreateForm;
